"use client";

import {
  Activity,
  AlertTriangle,
  Beaker,
  CheckCircle2,
  ChevronDown,
  ClipboardClock,
  Download,
  ExternalLink,
  FileText,
  HeartPulse,
  Images,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  PinOff,
  RefreshCw,
  ScanLine,
  Search,
  Stethoscope,
  Syringe,
  Waves,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { patient } from "@/src/mocks/patient";
import { useLang, localizedName } from "@/src/i18n";

type ViewerSection = "labs" | "vitals" | "conditions" | "pacs" | "notes" | "mar";
type ImagingItem = (typeof patient.imaging)[number];

type ClinicalViewerProps = {
  collapsed: boolean;
  onToggle: () => void;
};

const sectionMeta: { id: ViewerSection; label: string; icon: typeof Beaker; alert?: string }[] = [
  { id: "labs", label: "Labs & results", icon: Beaker, alert: "3 abnormal" },
  { id: "vitals", label: "Vital trends", icon: Activity },
  { id: "conditions", label: "Underlying conditions", icon: Stethoscope },
  { id: "pacs", label: "PACS & scanned documents", icon: Images, alert: "3 files" },
  { id: "notes", label: "Prior progress notes", icon: ClipboardClock },
  { id: "mar", label: "Medication administration", icon: Syringe },
];

function LabsPanel() {
  const { t } = useLang();
  return (
    <div className="viewer-detail">
      <div className="clinical-context-alert">
        <AlertTriangle size={16} />
        <div><strong>{t("cv.labs.hbDrop")}</strong><span>{t("cv.labs.hbDrop.sub")}</span></div>
      </div>
      <div className="result-head"><strong>CBC</strong><span>{t("cv.today")} · 08:00</span></div>
      <div className="lab-grid">
        {patient.labs.CBC.map((lab) => (
          <div key={lab.name} className={lab.abnormal ? "abnormal" : ""}>
            <span>{lab.name}</span><strong>{lab.value}</strong><small>{lab.unit}</small>
          </div>
        ))}
      </div>
      <div className="result-head"><strong>{t("cv.chemistry")}</strong><span>{t("cv.today")} · 08:00</span></div>
      <div className="lab-grid">
        {patient.labs.Chem7.map((lab) => (
          <div key={lab.name} className={lab.abnormal ? "abnormal" : ""}>
            <span>{lab.name}</span><strong>{lab.value}</strong><small>{lab.unit}</small>
          </div>
        ))}
      </div>
      <button className="text-link" type="button">{t("cv.compareHistory")}</button>
    </div>
  );
}

function VitalsPanel() {
  const { t } = useLang();
  return (
    <div className="viewer-detail">
      <div className="vital-summary">
        <div><span>{t("cv.vitals.bp")}</span><strong>{patient.vitals.bp}</strong><small>mmHg · {t("cv.vitals.stable")}</small></div>
        <div><span>SpO₂</span><strong>{patient.vitals.spo2}</strong><small>{t("cv.vitals.roomAir")}</small></div>
        <div><span>{t("cv.vitals.pulse")}</span><strong>{patient.vitals.pulse}</strong><small>bpm</small></div>
        <div><span>{t("cv.vitals.temp")}</span><strong>{patient.vitals.temp}</strong><small>{t("cv.vitals.latest")}</small></div>
      </div>
      <div className="sparkline" aria-label="Pulse trend over eight readings">
        {patient.vitals.series.map((value, index) => (
          <span key={`${value}-${index}`} style={{ height: `${20 + (value - 70) * 2.4}px` }} />
        ))}
      </div>
      <p className="micro-copy">{t("cv.vitals.micro")}</p>
    </div>
  );
}

function ConditionsPanel() {
  const { t } = useLang();
  return (
    <div className="viewer-detail">
      <div className="condition-list">
        {patient.diagnoses.map((diagnosis) => (
          <article key={diagnosis.code}>
            <span>{diagnosis.shortLabel}</span>
            <div><strong>{diagnosis.label}</strong><small>ICD-10 {diagnosis.code} · {t("cv.active")}</small></div>
          </article>
        ))}
      </div>
      <div className="history-note">
        <HeartPulse size={15} />
        <p>{t("cv.conditions.history")}</p>
      </div>
    </div>
  );
}

function NotesPanel() {
  const { t } = useLang();
  return (
    <div className="viewer-detail prior-notes">
      {patient.priorNotes.map((note) => (
        <article key={note.at}>
          <span>{note.at} · {note.author}</span>
          <p>{note.text}</p>
          <button type="button">{t("cv.notes.compare")}</button>
        </article>
      ))}
    </div>
  );
}

function MarPanel() {
  return (
    <div className="viewer-detail">
      <div className="mar-timeline">
        {patient.mar.map((item, index) => (
          <article key={item}>
            <span className={index === 3 ? "pending" : ""}>{index === 3 ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />}</span>
            <div><strong>{item.split(" — ")[0]}</strong><small>{item.split(" — ")[1]}</small></div>
          </article>
        ))}
      </div>
    </div>
  );
}

function AttachmentThumbnail({ item }: { item: ImagingItem }) {
  if (item.kind === "image") {
    return (
      <div className="attachment-thumb xray-thumb" aria-hidden="true">
        <span className="xray-spine" />
        <span className="xray-lung left" />
        <span className="xray-lung right" />
        <small>AP</small>
      </div>
    );
  }
  if (item.kind === "waveform") {
    return (
      <div className="attachment-thumb ecg-thumb" aria-hidden="true">
        <Waves size={34} />
        <span>25 mm/s</span>
      </div>
    );
  }
  return (
    <div className="attachment-thumb document-thumb" aria-hidden="true">
      <FileText size={31} />
      <i /><i /><i />
      <span>PDF</span>
    </div>
  );
}

function PacsPanel({ onPreview }: { onPreview: (item: ImagingItem) => void }) {
  const { t } = useLang();
  return (
    <div className="viewer-detail pacs-panel">
      <div className="pacs-summary">
        <ScanLine size={16} />
        <span><strong>{t("cv.pacs.connected")}</strong><small>{t("cv.pacs.summary")}</small></span>
      </div>
      <div className="attachment-grid">
        {patient.imaging.map((item) => (
          <button type="button" className="attachment-card" key={item.id} onClick={() => onPreview(item)}>
            <AttachmentThumbnail item={item} />
            <span>
              <strong>{item.title}</strong>
              <small>{item.subtitle}</small>
              <em>{item.status}</em>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AttachmentPreview({ item, onClose }: { item: ImagingItem; onClose: () => void }) {
  const { t, lang } = useLang();
  return (
    <div className="attachment-lightbox" role="presentation" onMouseDown={onClose}>
      <section
        className="attachment-preview"
        role="dialog"
        aria-modal="true"
        aria-labelledby="attachment-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="attachment-preview-head">
          <div><span>{t("cv.preview.context")}</span><h2 id="attachment-title">{item.title}</h2></div>
          <button type="button" onClick={onClose} aria-label="Close attachment preview"><X size={20} /></button>
        </div>
        <div className="attachment-preview-body">
          <div className={`large-clinical-preview ${item.kind}`}>
            <AttachmentThumbnail item={item} />
            {item.kind === "document" && (
              <div className="mock-document-copy">
                <strong>{t("cv.preview.referral")}</strong>
                <p>{t("cv.preview.referralBody")}</p>
                <i /><i /><i />
              </div>
            )}
          </div>
          <aside>
            <span className="file-status">{item.status}</span>
            <h3>{t("cv.preview.summary")}</h3>
            <p>{item.summary}</p>
            <dl>
              <div><dt>{t("cv.preview.source")}</dt><dd>{item.subtitle}</dd></div>
              <div><dt>{t("cv.preview.reportedBy")}</dt><dd>{item.author}</dd></div>
              <div><dt>{t("cv.preview.patient")}</dt><dd>{localizedName(lang, patient.name, patient.englishName)} · AN {patient.an}</dd></div>
            </dl>
            <div className="attachment-actions">
              <button type="button"><ExternalLink size={15} /> {t("cv.preview.openViewer")}</button>
              <button type="button"><Download size={15} /> {t("cv.preview.export")}</button>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

// Searchable free-text per section so the viewer search matches actual content
// (lab values, note text, medications, imaging) — not just section titles.
const sectionSearchText: Record<ViewerSection, string> = {
  labs: [
    "Hb decreased 8.1 7.2",
    ...patient.labs.CBC.map((l) => `${l.name} ${l.value} ${l.unit}`),
    ...patient.labs.Chem7.map((l) => `${l.name} ${l.value} ${l.unit}`),
  ].join(" ").toLowerCase(),
  vitals: `blood pressure ${patient.vitals.bp} spo2 ${patient.vitals.spo2} pulse ${patient.vitals.pulse} temperature ${patient.vitals.temp}`.toLowerCase(),
  conditions: patient.diagnoses
    .map((d) => `${d.shortLabel} ${d.label} ${d.code}`)
    .concat(patient.history)
    .join(" ")
    .toLowerCase(),
  pacs: patient.imaging.map((i) => `${i.title} ${i.subtitle} ${i.summary} ${i.status} ${i.author}`).join(" ").toLowerCase(),
  notes: patient.priorNotes.map((n) => `${n.author} ${n.text}`).join(" ").toLowerCase(),
  mar: patient.mar.join(" ").toLowerCase(),
};

export function ClinicalViewer({ collapsed, onToggle }: ClinicalViewerProps) {
  const { t } = useLang();
  const [active, setActive] = useState<ViewerSection | null>("labs");
  const [pinned, setPinned] = useState<ViewerSection[]>(["labs"]);
  const [query, setQuery] = useState("");
  const [selectedAttachment, setSelectedAttachment] = useState<ImagingItem | null>(null);

  const normalizedQuery = query.trim().toLowerCase();

  const visibleSections = useMemo(
    () =>
      sectionMeta.filter((section) => {
        if (!normalizedQuery) return true;
        return (
          section.label.toLowerCase().includes(normalizedQuery) ||
          sectionSearchText[section.id].includes(normalizedQuery)
        );
      }),
    [normalizedQuery],
  );

  // When searching, auto-open the first matching section so the hit is visible.
  useEffect(() => {
    if (normalizedQuery && visibleSections.length > 0) {
      setActive(visibleSections[0].id);
    }
  }, [normalizedQuery, visibleSections]);

  if (collapsed) {
    return (
      <section className="panel clinical-viewer is-collapsed">
        <button type="button" className="viewer-restore" onClick={onToggle} aria-label="Show Clinical Viewer">
          <PanelLeftOpen size={19} /><span>{t("cv.show")}</span>
        </button>
      </section>
    );
  }

  const panelFor = (section: ViewerSection) => {
    if (section === "labs") return <LabsPanel />;
    if (section === "vitals") return <VitalsPanel />;
    if (section === "conditions") return <ConditionsPanel />;
    if (section === "pacs") return <PacsPanel onPreview={setSelectedAttachment} />;
    if (section === "notes") return <NotesPanel />;
    return <MarPanel />;
  };

  const pinnedAwayFromActive = pinned.filter((section) => section !== active);

  function togglePin(section: ViewerSection) {
    setPinned((current) =>
      current.includes(section)
        ? current.filter((item) => item !== section)
        : [...current, section],
    );
  }

  return (
    <>
      <section className="panel clinical-viewer">
        <div className="panel-heading viewer-heading">
          <div><Activity size={18} /><span>{t("cv.title")}</span></div>
          <button type="button" onClick={onToggle} className="panel-collapse-button" aria-label="Hide Clinical Viewer">
            <PanelLeftClose size={16} /> {t("cv.hide")}
          </button>
        </div>

        <div className="viewer-toolbar">
          <div><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("cv.search")} aria-label="Search Clinical Viewer" /></div>
          <span><RefreshCw size={12} /> {t("cv.synced")}</span>
        </div>

        {pinnedAwayFromActive.length > 0 && (
          <div className="pinned-context" aria-label="Pinned clinical context">
            <div className="pinned-context-head"><Pin size={13} /><span>{t("cv.pinnedHead")}</span></div>
            {pinnedAwayFromActive.map((section) => {
              const meta = sectionMeta.find((item) => item.id === section)!;
              return (
                <article className="pinned-context-card" key={section}>
                  <div><strong>{t(`cv.section.${meta.id}`)}</strong><button type="button" onClick={() => togglePin(section)}><PinOff size={13} /> {t("cv.unpin")}</button></div>
                  {panelFor(section)}
                </article>
              );
            })}
          </div>
        )}

        <div className="accordion">
          {visibleSections.map(({ id, label, icon: Icon, alert }) => {
            const expanded = active === id;
            const isPinned = pinned.includes(id);
            return (
              <div className={`accordion-item ${expanded ? "is-open" : ""}`} key={id}>
                <div className="accordion-trigger">
                  <button type="button" onClick={() => setActive(expanded ? null : id)} aria-expanded={expanded}>
                    <Icon size={17} /><span>{t(`cv.section.${id}`)}</span>{alert && <em>{t(`cv.alert.${id}`)}</em>}<ChevronDown size={16} />
                  </button>
                  <button
                    type="button"
                    className={`pin-button ${isPinned ? "is-pinned" : ""}`}
                    onClick={() => togglePin(id)}
                    aria-label={isPinned ? `Unpin ${label}` : `Pin ${label}`}
                    title={isPinned ? "Remove from pinned context" : "Keep visible while opening other tabs"}
                  >
                    {isPinned ? <><PinOff size={14} /><span>{t("cv.pinned")}</span></> : <><Pin size={14} /><span>{t("cv.pin")}</span></>}
                  </button>
                </div>
                {expanded && panelFor(id)}
              </div>
            );
          })}
          {visibleSections.length === 0 && (
            <div className="viewer-empty">
              <Search size={16} /> {t("cv.empty")} “{query}”.
              <button type="button" onClick={() => setQuery("")}>{t("cv.clearSearch")}</button>
            </div>
          )}
        </div>
      </section>
      {selectedAttachment && <AttachmentPreview item={selectedAttachment} onClose={() => setSelectedAttachment(null)} />}
    </>
  );
}
